import React, {useState} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {useSiteContent} from '../../hooks/useSiteContent'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiChevronLeft, FiChevronRight, FiStar, FiQuote} = FiIcons

const TestimonialsSection = ({content = {}}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const {content: siteContent} = useSiteContent()

  const {
    title = "Real Results from Real People",
    testimonials = []
  } = content

  const defaultTestimonials = [
    {
      name: "Sarah M.",
      result: "Lost 35 lbs in 6 months",
      quote: "Finally found an approach that works with my crazy schedule. No more yo-yo dieting!",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Mike R.",
      result: "Down 28 lbs, kept it off 2 years",
      quote: "The PPC method is so simple but incredibly effective. This changed my relationship with food.",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Jennifer K.",
      result: "Lost 42 lbs, gained confidence", 
      quote: "I love that this focuses on habits, not restriction. I actually enjoy eating healthy now!",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
    }
  ]

  const testimonialsToShow = testimonials.length > 0 ? testimonials : defaultTestimonials

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonialsToShow.length)
  }

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonialsToShow.length) % testimonialsToShow.length)
  }

  if (testimonialsToShow.length === 0) return null

  return (
    <section className="py-20 bg-gradient-to-br from-nutrition-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <div className="flex justify-center space-x-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <SafeIcon key={i} icon={FiStar} className="w-6 h-6 text-yellow-400 fill-current" />
            ))}
          </div>
          <p className="text-gray-600">
            Based on {siteContent?.reviews_display_text || '50+ verified reviews on Google'}
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center text-nutrition-600 hover:bg-nutrition-50"
          >
            <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
          </button>

          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center text-nutrition-600 hover:bg-nutrition-50"
          >
            <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
          </button>

          {/* Testimonial Cards */}
          <div className="overflow-hidden rounded-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{opacity: 0, x: 50}}
                animate={{opacity: 1, x: 0}}
                exit={{opacity: 0, x: -50}}
                transition={{duration: 0.5}}
                className="bg-white rounded-2xl shadow-xl p-8 md:p-12 relative"
              >
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 w-12 h-12 bg-nutrition-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiQuote} className="w-6 h-6 text-nutrition-600" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                  {/* Profile */}
                  <div className="text-center md:text-left">
                    <div className="relative inline-block mb-4">
                      <img
                        src={testimonialsToShow[currentIndex].image}
                        alt={testimonialsToShow[currentIndex].name}
                        className="w-20 h-20 rounded-full object-cover mx-auto md:mx-0"
                      />
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-nutrition-600 rounded-full flex items-center justify-center">
                        <SafeIcon icon={FiStar} className="w-4 h-4 text-white fill-current" />
                      </div>
                    </div>
                    <h4 className="font-bold text-lg text-gray-900 mb-1">
                      {testimonialsToShow[currentIndex].name}
                    </h4>
                    <p className="text-nutrition-600 font-semibold text-sm">
                      {testimonialsToShow[currentIndex].result}
                    </p>
                  </div>

                  {/* Quote */}
                  <div className="md:col-span-2">
                    <blockquote className="text-xl md:text-2xl text-gray-700 font-medium leading-relaxed italic">
                      "{testimonialsToShow[currentIndex].quote}"
                    </blockquote>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonialsToShow.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-nutrition-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Stats Row */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.3}}
          viewport={{once: true}}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {[
            {number: siteContent?.reviews_display_text?.split('+')[0] + '+' || "50+", label: "Verified Reviews"},
            {number: "92%", label: "Keep Weight Off"},
            {number: "4.9★", label: `${siteContent?.reviews_source_label || 'Google'} Rating`}
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl font-bold text-nutrition-600 mb-2">{stat.number}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default TestimonialsSection